import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getExtAdminDashboard } from "../../../redux/actions/profile";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const COLORS = ["#a7cc48", "#4fb0c6", "#f4a261", "#e63946", "#9b5de5"];

function StatCard({ label, value, icon, sub }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardIcon}><i className={icon} style={{ fontSize: 22, color: PRIMARY }} /></div>
      <div>
        <div style={styles.cardValue}>{value ?? "—"}</div>
        <div style={styles.cardLabel}>{label}</div>
        {sub && <div style={styles.cardSub}>{sub}</div>}
      </div>
    </div>
  );
}

export default function Overview() {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.profile.extAdminDashboard);

  useEffect(() => {
    dispatch(getExtAdminDashboard());
  }, [dispatch]);

  if (!data) {
    return (
      <div style={styles.empty}>
        <i className="bi bi-bar-chart-line" style={{ fontSize: 48, color: PRIMARY }} />
        <p style={{ color: "#888", marginTop: 12 }}>Loading dashboard…</p>
      </div>
    );
  }

  const agentStatusData = [
    { name: "Approved", value: data.approvedAgents },
    { name: "Pending", value: data.pendingAgents },
  ];

  return (
    <div style={styles.root}>
      <h2 style={styles.heading}>Dashboard Overview</h2>
      {data.adminRefCode && (
        <div style={styles.refBadge}>
          <i className="bi bi-link-45deg" style={{ marginRight: 6, color: PRIMARY }} />
          Your Referral Code: <strong style={{ color: PRIMARY, marginLeft: 6, letterSpacing: 2 }}>{data.adminRefCode}</strong>
        </div>
      )}

      <div style={styles.cardGrid}>
        <StatCard label="Total Agents" value={data.totalAgents} icon="bi bi-people-fill" />
        <StatCard label="Approved Agents" value={data.approvedAgents} icon="bi bi-person-check-fill" />
        <StatCard label="Pending Approval" value={data.pendingAgents} icon="bi bi-person-exclamation" />
        <StatCard label="Total Referrals" value={data.totalReferrals} icon="bi bi-megaphone-fill"
          sub={"You: " + data.adminDirectReferrals + " · Agents: " + data.agentReferrals} />
        <StatCard label="Total Commission" value={"₦" + (data.totalCommission || 0).toLocaleString()} icon="bi bi-cash-coin"
          sub={"Direct: ₦" + (data.adminCommission || 0).toLocaleString()} />
      </div>

      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Monthly Referrals (last 6 months)</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.monthlyReferrals}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis tick={{ fill: "#666", fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="referrals" stroke={PRIMARY} strokeWidth={2} dot={{ fill: PRIMARY }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Agents by Sector</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.sectorData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="sector" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis tick={{ fill: "#666", fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={PRIMARY} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Agent Status</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={agentStatusData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                label={({ name, value }) => name + ": " + value}>
                {agentStatusData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "0 0 40px 0" },
  heading: { fontSize: 22, fontWeight: 700, color: BG, marginBottom: 8 },
  refBadge: {
    display: "inline-flex", alignItems: "center", background: "#fff",
    border: "1.5px solid " + PRIMARY, borderRadius: 6, padding: "6px 16px",
    fontSize: 14, color: "#444", marginBottom: 24
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: 16, marginBottom: 32
  },
  card: {
    background: "#fff", borderRadius: 10, padding: "18px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)", display: "flex",
    alignItems: "flex-start", gap: 14
  },
  cardIcon: {
    width: 44, height: 44, borderRadius: 10,
    background: "rgba(167,204,72,0.12)", display: "flex",
    alignItems: "center", justifyContent: "center", flexShrink: 0
  },
  cardValue: { fontSize: 24, fontWeight: 700, color: BG, lineHeight: 1.2 },
  cardLabel: { fontSize: 12, color: "#888", marginTop: 2 },
  cardSub: { fontSize: 11, color: "#aaa", marginTop: 4 },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 20
  },
  chartCard: {
    background: "#fff", borderRadius: 10, padding: "20px 16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)"
  },
  chartTitle: { fontSize: 14, fontWeight: 600, color: BG, marginBottom: 12 },
  empty: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: 300
  },
};
